import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  IconButton,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  MenuItem,
  Card,
} from "@mui/material";
import {
  StyledButton,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledMultilineTextField,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { durationType } from "../initialData/initialData";
import { getHumanReadableDiffInPastDate } from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";

const SurgicalHistorySimplified = ({
  surgicalHistoryData,
  setSurgicalHistoryData,
  activeHistorySection,
  setActiveHistorySection,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  console.log("Surgical History called", surgicalHistoryData);
  const today = dayjs();
  const [surgicalHistoryVars, setSurgicalHistoryVars] = useState({
    procedure: "",
    id: null,
    follow_up_description: "",
    recorded_date: null,
    clinical_note: "",
    duration: "",
    durationType: "days",
  });
  const [procedureName, setProcedureName] = useState("");
  const [error, setError] = useState("");
  const [surgicalHistoryEditIndex, setSurgicalHistoryEditIndex] =
    useState(null);

  console.log("surgicalHistoryVars", surgicalHistoryVars);
  const handleAddRow = async () => {
    console.log("Handle Add Called", surgicalHistoryVars);
    if (surgicalHistoryVars.procedure || procedureName.length > 0) {
      // Store the updated state in a local variable

      let newDetails = {
        ...surgicalHistoryVars,
        id: null,
      };

      const newRow = {
        procedure: newDetails.procedure,
        id: newDetails.id,
        follow_up_description: newDetails.follow_up_description,
        recorded_date: newDetails?.recorded_date?.format("YYYY-MM-DD"),
        clinical_note: newDetails?.clinical_note,
        duration: newDetails?.duration,
        durationType: newDetails?.durationType,
      };

      // Update family history rows by adding the new row

      const updatedRows = [...surgicalHistoryData["new"], newRow];

      setSurgicalHistoryData({
        ...surgicalHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setSurgicalHistoryVars({
        ...surgicalHistoryVars,
        procedure: "",
        id: null,
        follow_up_description: "",
        clinical_note: "",
        duration: "",
        durationType: "days",
      });
      setProcedureName("");
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Procedure Name field is required");
    }
  };
  const handleSurgicalHistoryEditClick = (row, rowIndex) => {
    setProcedureName(row.procedure);
    setSurgicalHistoryVars({
      procedure: "",
      id: null,
      recorded_date: row.recorded_date ? dayjs(row.recorded_date) : null,
      follow_up_description: row.follow_up_description,
      clinical_note: row.clinical_note,
      duration: row?.duration,
      durationType: row?.durationType,
    });
    setSurgicalHistoryEditIndex(rowIndex);
  };
  const handleSurgicalHistoryUpdate = async () => {
    if (surgicalHistoryVars.procedure || procedureName.length > 0) {
      // Store the updated state in a local variable

      let newDetails;
      newDetails = { ...surgicalHistoryVars, source: undefined };

      const updatedRow = {
        procedure: newDetails.procedure,
        id: newDetails.id,
        follow_up_description: newDetails.follow_up_description,
        recorded_date: newDetails?.recorded_date?.format("YYYY-MM-DD"),
        clinical_note: newDetails?.clinical_note,
        duration: newDetails?.duration,
        durationType: newDetails?.durationType,
        source: undefined,
      };

      // Update family history rows by adding the new row

      const updatedSurgicalHistory = surgicalHistoryData["new"].map(
        (row, index) => {
          if (index === surgicalHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setSurgicalHistoryData({
        ...surgicalHistoryData,
        new: updatedSurgicalHistory,
      });
      // Reset family history variables and clear error
      setSurgicalHistoryVars({
        ...surgicalHistoryVars,
        procedure: "",
        id: null,
        follow_up_description: "",
        clinical_note: "",
        duration: "",
        durationType: "days",
      });
      setProcedureName("");
      setError("");
      setSurgicalHistoryEditIndex(null);
    } else {
      // Set error message if any field is missing
      setError("Procedure Name field is required");
    }
  };
  const handleSurgicalHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedSurgicalHistory = surgicalHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setSurgicalHistoryData({
      ...surgicalHistoryData,
      new: updatedSurgicalHistory,
    });
  };
  const handleChangeSurgicalHistory = (field, value) => {
    let updatedDetails = {
      ...surgicalHistoryVars,
      [field]: field === "procedure" ? value.term : value,
    };

    // If field is duration or durationType, calculate recorded_date
    if (
      (field === "duration" || field === "durationType") &&
      updatedDetails.duration &&
      updatedDetails.durationType
    ) {
      const calculatedDate = calculateOnsetDate(
        parseInt(updatedDetails.duration),
        updatedDetails.durationType
      );
      updatedDetails.recorded_date = calculatedDate ?? null;
    } else if (field === "duration" || field === "durationType") {
      updatedDetails.recorded_date = null;
    }

    setSurgicalHistoryVars(updatedDetails);
  };

  const calculateOnsetDate = (duration, type) => {
    if (!duration) return null;
    const today = dayjs(); // Current date
    const durationMap = {
      minutes: "minute",
      hours: "hour",
      days: "day",
      weeks: "week",
      months: "month",
      years: "year",
    };
    console.log("date onset", today.subtract(duration, durationMap[type]));
    return today.subtract(duration, durationMap[type]);
  };

  const handleClearProcedureName = () => {
    setError("");
    setProcedureName("");
    setSurgicalHistoryVars({
      ...surgicalHistoryVars,
      procedure: "",
    });
  };
  //    const handleAddOption = async (term) => {
  //     const postBody = {
  //       term: term,
  //       state: "active",
  //       semantictag: "procedure",
  //       acceptability: "synonyms",
  //       returnlimit: "-1",
  //       refsetid: null,
  //       parentid: null,
  //       excludeparentconcept: false,
  //       fullconcept: true,
  //     };

  //     try {
  //       if (procedureName.length > 2) {
  //         const response = await axios.post(
  //           `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //           postBody,
  //           { headers: headers }
  //         );
  //         if (response) {
  //           let updatedFound = {},
  //             updatedSelectedOptiondetsils = {},
  //             foundData = false;
  //           console.log("Snomed Response", response);
  //           if (
  //             response.data.status === "Success" &&
  //             response.data.data.length > 0
  //           ) {
  //             foundData = response.data.data.find((d) => d.term === term);
  //             console.log("Found Data", foundData);
  //             if (foundData) {
  //               updatedFound = {
  //                 ...foundData,
  //                 found: true,
  //                 id: foundData.conceptId,
  //               };
  //             } else {
  //               updatedSelectedOptiondetsils = {
  //                 hierarchy: null,
  //                 isPreferredTerm: null,
  //                 conceptState: null,
  //                 conceptFsn: term,
  //                 definitionStatus: null,
  //                 conceptId: null,
  //                 languageCode: "en",
  //                 typeId: null,
  //                 term: term,
  //                 caseSignificanceId: "CASE_INSENSITIVE",
  //                 id: null,
  //                 effectiveTime: null,
  //                 activeStatus: null,
  //                 moduleId: null,
  //               };
  //             }
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: term,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: term,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }

  //           setAutoCompleteData([]);
  //           setFilteredOptions([]);
  //           setSuggestApiCalled(false);
  //           setDebounceFunctionCalledProcedure(true);
  //           if (foundData) {
  //             handleChangeSurgicalHistory("procedure", updatedFound);
  //           } else {
  //             handleChangeSurgicalHistory(
  //               "procedure",
  //               updatedSelectedOptiondetsils
  //             );
  //           }
  //         }
  //       }
  //     } catch (err) {
  //       console.log(err);
  //       toast.error(err);
  //     }
  //   };
  const handleClearClinicalNotes = () => {
    setError("");
    setSurgicalHistoryVars({
      ...surgicalHistoryVars,
      clinical_note: "", // Use computed property name to set the field dynamically
    });
  };

  const handleConditionTyped = (value) => {
    setProcedureName(value);
    handleChangeSurgicalHistory("procedure", { term: value, id: null });
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={11} md={11} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            label="Procedure Name"
            variant="outlined"
            value={procedureName}
            onChange={(e) => {
              handleConditionTyped(e.target.value);
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearProcedureName}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          <StyledTextField
            // id="outlined-multiline-static"
            variant="outlined"
            label="Clinical Notes"
            // multiline
            // rows={2}
            value={surgicalHistoryVars.clinical_note}
            onChange={(e) =>
              handleChangeSurgicalHistory("clinical_note", e.target.value)
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearClinicalNotes}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>

                  <>
                    {/* {listeningOn === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="defaultColor"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : listeningOn === true &&
                      clinicalNotesListening === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="default"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Stop Listening">
                        <OutlinedIconFab
                          onClick={() => {
                            SpeechRecognition.stopListening();
                            setListeningOn(false);
                            setClinicalNotesListening(false);
                          }}
                          variant="outlined"
                          color="default"
                          size="small"
                        >
                          <KeyboardVoiceIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    )} */}
                  </>
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          <StyledTextField
            // id="outlined-multiline-static"
            variant="outlined"
            label="Follow Up Description"
            // multiline
            // rows={2}
            value={surgicalHistoryVars.follow_up_description}
            onChange={(e) =>
              handleChangeSurgicalHistory(
                "follow_up_description",
                e.target.value
              )
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={6} sm={4} md={4} style={{ display: "flex" }}>
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            Time Since Surgery:
          </span>

          <StyledTextField
            label="Duration"
            fullWidth
            type="number"
            value={surgicalHistoryVars.duration || ""}
            // error={!!validationErrors.duration}
            // helperText={validationErrors.duration}
            onChange={(e) =>
              handleChangeSurgicalHistory("duration", e.target.value)
            }
            onWheel={(e) => e.target.blur()} // Prevent scroll change
            InputProps={{
              inputProps: {
                style: {
                  // Hide arrows in Chrome, Safari, Edge
                  MozAppearance: "textfield",
                },
              },
              sx: {
                // Hide arrows in Firefox
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                // Hide arrows in Chrome, Safari, Edge
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={5} sm={4} md={4}>
          <StyledTextField
            select
            fullWidth
            label="Duration Type"
            value={surgicalHistoryVars.durationType || "days"}
            onChange={(e) =>
              handleChangeSurgicalHistory("durationType", e.target.value)
            }
          >
            {durationType.map((type) => (
              <MenuItem
                key={type.value}
                value={type.value}
                sx={{ fontSize: "12px" }}
              >
                {type.display}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={11} sm={4} md={4} sx={{ display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Recorded Date"
                onChange={(date) =>
                  handleChangeSurgicalHistory("recorded_date", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  surgicalHistoryVars?.recorded_date
                    ? dayjs(surgicalHistoryVars?.recorded_date)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={11} sm={3} md={3}>
          <StyledButton
            bgColor="#0E3F66"
            onClick={
              surgicalHistoryEditIndex !== null
                ? handleSurgicalHistoryUpdate
                : handleAddRow
            }
            variant="contained"
            fullWidth
            color="defaultColor"
          >
            {surgicalHistoryEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </Grid>
        <Grid item xs={11} sm={11} md={11}>
          {surgicalHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Procedure Name
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Follow Up Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Time Since Surgery
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {surgicalHistoryData["prev"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      <StyledTableCellHistory>
                        {row?.procedure}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.clinical_note || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.follow_up_description || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.recorded_date
                          ? getHumanReadableDiffInPastDate(row?.recorded_date)
                          : "-"}
                      </StyledTableCellHistory>

                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11} md={11}>
          {surgicalHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Procedure Name
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Clinical Notes
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Follow Up Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Time Since Surgery
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {surgicalHistoryData["new"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {/* {console.log("Surgical History row", row)} */}
                      <StyledTableCellHistory>
                        {row?.procedure}
                        {row?.source === "voice" && !row.id && (
                          <Tooltip title="Machine-interpreted text — please verify before confirming">
                            <span className="missing-id-warning">⚠️</span>
                          </Tooltip>
                        )}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.clinical_note || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.follow_up_description || "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {row?.duration
                          ? `${row?.duration} ${row?.durationType}`
                          : "-"}
                      </StyledTableCellHistory>
                      <StyledTableCellHistory>
                        {surgicalHistoryData["prev"].includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleSurgicalHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleSurgicalHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default SurgicalHistorySimplified;
